import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { NotificationItem, isUnread } from '../hooks/use-notifications-api.hook';
import { CATEGORY_ICONS, SEVERITY_DOT, notificationTitle } from '../../modules/notifications/notification-presentation';

dayjs.extend(relativeTime);

interface NotificationListProps {
  items: NotificationItem[];
  onNavigate: (route: string) => void;
  /** Called when a row is opened, before navigating — to mark it read. */
  onOpen?: (item: NotificationItem) => void;
  /** The page shows the body in full; the bell keeps rows to two lines. */
  variant?: 'popover' | 'page';
}

/**
 * Notifications as rows. The words come rendered from the API (one catalog for
 * the bell, email and chat channels), so an unfamiliar type still reads as a
 * sentence — the old client-side map showed "insights.weekly" for anything it
 * had not been taught.
 */
export function NotificationList({ items, onNavigate, onOpen, variant = 'popover' }: NotificationListProps) {
  const { t } = useTranslation();
  const page = variant === 'page';

  return (
    <div className={cn(!page && 'max-h-[420px] overflow-y-auto')}>
      {items.map((item) => {
        const unread = isUnread(item);
        const route = item.route || null;
        const Icon = CATEGORY_ICONS[item.category ?? 'other'] ?? CATEGORY_ICONS.other;
        const dot = SEVERITY_DOT[item.severity];
        const open = () => {
          onOpen?.(item);
          if (route) onNavigate(route);
        };

        return (
          <div
            key={item._id}
            role={route || onOpen ? 'button' : undefined}
            tabIndex={route || onOpen ? 0 : undefined}
            onClick={route || onOpen ? open : undefined}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && (route || onOpen)) {
                e.preventDefault();
                open();
              }
            }}
            data-testid="notification-row"
            data-unread={unread || undefined}
            className={cn(
              'group flex gap-3 border-b px-4 py-3 last:border-b-0 outline-none focus-visible:bg-accent',
              (route || onOpen) && 'cursor-pointer hover:bg-accent',
              page && 'px-5 py-4',
            )}
          >
            <span className="relative mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg border bg-background text-muted-foreground">
              <Icon className="size-4" aria-hidden />
              {dot && <span className={cn('absolute -end-0.5 -top-0.5 size-2 rounded-full ring-2 ring-background', dot)} aria-hidden />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-2">
                <p className={cn('min-w-0 flex-1 text-sm leading-snug', unread ? 'font-medium text-foreground' : 'text-muted-foreground')}>
                  {notificationTitle(item, t('ARDI.NOTIFICATIONS.FALLBACK_TITLE'))}
                </p>
                {unread && <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-foreground" aria-label={t('ARDI.NOTIFICATIONS.UNREAD') as string} />}
              </div>
              {item.body && <p className={cn('text-xs leading-snug text-muted-foreground', !page && 'line-clamp-2')}>{item.body}</p>}
              {item.detail && <p className="truncate text-xs leading-snug text-muted-foreground/80 tabular-nums">{item.detail}</p>}
              <p className="mt-1 text-[11px] text-muted-foreground">{dayjs(item.createdAt).fromNow()}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
